import * as THREE from 'three'

/** A collision body is an authored, moving object that must have a physical
 * relationship with the other moving objects in a shot. Static scenery is kept
 * out of this list because the actors are intentionally staged inside the
 * restaurant, wreck, and throne set.
 */
export type CollisionBodyKind = 'actor' | 'story-prop' | 'set-prop'

export interface CollisionBody {
  object: THREE.Object3D
  name: string
  kind: CollisionBodyKind
  purpose: string
}

export interface CollisionBounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

export interface CollisionProxy {
  center: [number, number, number]
  halfSize: [number, number, number]
}

export function boundsOf(object: THREE.Object3D): CollisionBounds {
  // updateMatrixWorld(true) only refreshes descendants. During a collision
  // pass a body may live below a stage/set group whose matrix was changed by
  // the previous subsystem, so a stale ancestor can make the AABB look as if
  // the correction never happened. updateWorldMatrix(true, true) refreshes
  // the complete chain before measuring the actual world-space volume.
  object.updateWorldMatrix(true, true)
  const proxy = object.userData.collisionProxy as CollisionProxy | undefined
  const box = proxy
    ? new THREE.Box3(
      new THREE.Vector3(proxy.center[0] - proxy.halfSize[0], proxy.center[1] - proxy.halfSize[1], proxy.center[2] - proxy.halfSize[2]),
      new THREE.Vector3(proxy.center[0] + proxy.halfSize[0], proxy.center[1] + proxy.halfSize[1], proxy.center[2] + proxy.halfSize[2]),
    ).applyMatrix4(object.matrixWorld)
    : new THREE.Box3().setFromObject(object)
  return { minX: box.min.x, maxX: box.max.x, minY: box.min.y, maxY: box.max.y, minZ: box.min.z, maxZ: box.max.z }
}

export function verticalOverlap(a: CollisionBounds, b: CollisionBounds, padding = 0): boolean {
  return a.minY < b.maxY - padding && a.maxY > b.minY + padding
}

export function horizontalOverlap(a: CollisionBounds, b: CollisionBounds, padding = 0): boolean {
  return a.minX < b.maxX - padding && a.maxX > b.minX + padding
    && a.minZ < b.maxZ - padding && a.maxZ > b.minZ + padding
}

/** Push a moving root out of a blocker along the shallowest horizontal axis.
 * All cutscene dynamic roots are direct children of their stage group, so the
 * correction is expressed in world axes and cannot be rotated by a parent.
 */
export function separateXZ(moving: CollisionBody, blocker: CollisionBody, padding = 0.055): boolean {
  const a = boundsOf(moving.object)
  const b = boundsOf(blocker.object)
  if (!verticalOverlap(a, b) || !horizontalOverlap(a, b)) return false
  const overlapX = Math.min(a.maxX - b.minX, b.maxX - a.minX)
  const overlapZ = Math.min(a.maxZ - b.minZ, b.maxZ - a.minZ)
  if (!(overlapX > 0 && overlapZ > 0)) return false
  const ac = new THREE.Vector3((a.minX + a.maxX) / 2, 0, (a.minZ + a.maxZ) / 2)
  const bc = new THREE.Vector3((b.minX + b.maxX) / 2, 0, (b.minZ + b.maxZ) / 2)
  if (overlapX <= overlapZ) {
    moving.object.position.x += (ac.x <= bc.x ? -1 : 1) * (overlapX + padding)
  } else {
    moving.object.position.z += (ac.z <= bc.z ? -1 : 1) * (overlapZ + padding)
  }
  moving.object.updateWorldMatrix(true, true)
  return true
}

export function overlapDepth(a: CollisionBody, b: CollisionBody): number {
  const aa = boundsOf(a.object)
  const bb = boundsOf(b.object)
  if (!verticalOverlap(aa, bb) || !horizontalOverlap(aa, bb)) return 0
  return Math.min(
    Math.min(aa.maxX - bb.minX, bb.maxX - aa.minX),
    Math.min(aa.maxZ - bb.minZ, bb.maxZ - aa.minZ),
  )
}
